import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";

interface BetaTagProps {
  /** Size step, matched to the text beside it. Default "medium". */
  size?: "small" | "medium" | "large";
  /**
   * Form. Default "outlined".
   *  - "outlined" transparent fill + hairline border (the quiet default)
   *  - "filled"   gray[100] (light) / gray[700] (dark), no border
   */
  variant?: "outlined" | "filled" | "filledDarkOnly";
  className?: string;
}

/**
 * BetaTag — marks a Plato feature as in-beta.
 *
 * The wordmark is "βeta": a Greek beta (U+03B2) opens the word and is set
 * one weight heavier, so it reads as "beta" in full while nodding to
 * Plato's namesake. The β leads by WEIGHT only — it inherits the same
 * color as "eta", so no letter looks mismatched. The "eta" completes it,
 * so there's no bare-β / German-ß misread.
 *
 * Dark mode is handled via the theme's `t.dark` flag — no separate props.
 *
 * @example
 * ```tsx
 * <BetaTag />                         // outlined, medium — the default
 * <BetaTag variant="filled" />        // filled
 * <BetaTag size="small" />            // dense rows, attribution lines
 * ```
 */
function BetaTag({ size = "medium", variant = "outlined", className }: BetaTagProps) {
  return (
    <Styled.Tag size={size} variant={variant} className={className}>
      <Styled.Lead>β</Styled.Lead>eta
    </Styled.Tag>
  );
}

export default BetaTag;

/* Styled Components
======================================================= */
let Styled: any;
Styled = {};

Styled.Tag = styled.div<{ size: string; variant: string }>((props) => {
  const t: any = props.theme;
  const { size, variant } = props;

  const sizeStyles = {
    small: css`
      ${t.rounded.xs}
      padding: 0.125rem 0.375rem;
      font-size: 0.6875rem;
    `,
    medium: css`
      ${t.rounded.xs}
      padding: 0.1875rem 0.5rem;
      font-size: 0.75rem;
    `,
    large: css`
      ${t.rounded.sm}
      padding: 0.25rem 0.625rem;
      font-size: 0.875rem;
    `,
  };

  const outlined = css`
    background: transparent;
    border: 1px solid ${t.dark ? t.color.gray[600] : t.color.gray[300]};
    color: ${t.dark ? t.color.gray[200] : t.color.gray[600]};
  `;

  const filled = css`
    border: 1px solid transparent;
    background: ${t.dark ? t.color.gray[700] : t.color.gray[100]};
    color: ${t.dark ? t.color.gray[200] : t.color.gray[600]};
  `;

  return css`
    label: BetaTag;
    margin-left: 2px;
    ${sizeStyles[size as keyof typeof sizeStyles]}
    ${variant === "filled" ? filled : variant === "filledDarkOnly" ? (t.dark ? filled : outlined) : outlined}
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    font-weight: 400;
    line-height: 1;
    white-space: nowrap;
    flex-shrink: 0;
    vertical-align: middle;
    user-select: none;
    ${t.dark &&
    css`
      letter-spacing: 0.04em;
    `}
  `;
});

Styled.Lead = styled.span((props) => {
  const t: any = props.theme;
  return css`
    label: BetaTag_Lead;
    color: inherit;
    font-weight: inherit;
    font-style: normal;
    ${t.dark &&
    css`
      font-weight: 450;
    `}
  `;
});
